import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Settings, Eye, PlayCircle, Bell, Trash2, ShieldAlert } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { GoogleCalendarIntegration } from './GoogleCalendarIntegration';
import PractitionerPasswordForm from './Security/PractitionerPasswordForm';
import PractitionerAddPasswordForm from './Security/PractitionerAddPasswordForm';
import { PractitionerReferralSettings } from './PractitionerReferralSettings';
import { getOnboardingStorageKey } from '@/components/onboarding/useOnboarding';
import SmsConsentForm from '@/components/shared/SmsConsentForm';
import { logConsent, logDataProcessing } from '@/lib/compliance-logger';
import DataRightsSection from '@/components/shared/DataRightsSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface PractitionerSettingsProps {
  practitionerId: string;
  profile: any;
  setProfile: (profile: any) => void;
}

const PractitionerSettings: React.FC<PractitionerSettingsProps> = ({ 
  practitionerId, 
  profile, 
  setProfile 
}) => {
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const [isSaving, setIsSaving] = useState(false);
  const [acceptingNewClients, setAcceptingNewClients] = useState<boolean>(profile?.accepting_new_clients ?? true);
  const [hasPasswordAuth, setHasPasswordAuth] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    new_booking_email: true,
    booking_cancellation_email: true,
    client_message_email: true,
    payout_notification_email: true,
    sms_appointment_reminder: false,
    sms_booking_update: false,
    sms_consent_at: null as string | null,
  });

  // Check if user has password authentication
  useEffect(() => {
    const checkAuthMethods = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const hasPassword = user.app_metadata?.providers?.includes('email') || 
                           user.identities?.some(identity => identity.provider === 'email');
        setHasPasswordAuth(hasPassword);
      }
    };
    checkAuthMethods();
  }, []);

  // Load notification preferences from profile
  useEffect(() => {
    if (profile?.notification_prefs) {
      try {
        const prefs = typeof profile.notification_prefs === 'string'
          ? JSON.parse(profile.notification_prefs)
          : profile.notification_prefs;
        setNotificationPrefs({
          new_booking_email: prefs.new_booking_email ?? true,
          booking_cancellation_email: prefs.booking_cancellation_email ?? true,
          client_message_email: prefs.client_message_email ?? true,
          payout_notification_email: prefs.payout_notification_email ?? true,
          sms_appointment_reminder: prefs.sms_appointment_reminder ?? false,
          sms_booking_update: prefs.sms_booking_update ?? false,
          sms_consent_at: prefs.sms_consent_at ?? null,
        });
      } catch (error) {
        console.error('Error parsing notification preferences:', error);
      }
    }
  }, [profile]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({
          accepting_new_clients: acceptingNewClients
        })
        .eq('id', practitionerId);

      if (error) throw error;

      // Optimistically update the profile
      setProfile(prevProfile => ({
        ...prevProfile,
        accepting_new_clients: acceptingNewClients
      }));

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Save notification preferences
  const saveNotificationPreferences = async () => {
    setNotifSaving(true);
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({ notification_prefs: notificationPrefs })
        .eq('id', practitionerId);

      if (error) throw error;

      setProfile(prev => ({ ...prev, notification_prefs: notificationPrefs }));
      toast({
        title: t('notifications.preferencesSaved', { defaultValue: 'Preferences Saved' }),
        description: t('notifications.preferencesSavedDescription', { defaultValue: 'Your notification preferences have been updated.' }),
      });
    } catch (error) {
      console.error('Save notification prefs error:', error);
      toast({
        title: t('notifications.saveFailed', { defaultValue: 'Update Failed' }),
        description: error instanceof Error ? error.message : 'Failed to save preferences',
        variant: 'destructive',
      });
    } finally {
      setNotifSaving(false);
    }
  };

  // Reset onboarding tour
  const resetOnboardingTour = () => {
    localStorage.removeItem(getOnboardingStorageKey('practitioner'));
    localStorage.removeItem(getOnboardingStorageKey('practitioner', practitionerId));
    window.location.reload();
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            General Settings
          </CardTitle>
          <CardDescription>
            Manage your basic profile settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="acceptingNewClients" className="flex items-center text-sm">
              <Eye className="w-4 h-4 mr-2" />
              Accepting New Clients
            </Label>
            <div className="space-y-1">
              <Switch
                id="acceptingNewClients"
                checked={acceptingNewClients}
                onCheckedChange={(checked) => setAcceptingNewClients(checked)}
              />
              <p className="text-xs text-muted-foreground">
                Control whether your availability is visible to clients.
              </p>
            </div>
          </div>



          <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full sm:w-auto">
            {isSaving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-pulse" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Security - Add/Change Password */}
      {!hasPasswordAuth ? (
        <PractitionerAddPasswordForm onPasswordAdded={() => setHasPasswordAuth(true)} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Update your account password.</CardDescription>
          </CardHeader>
          <CardContent>
            <PractitionerPasswordForm />
          </CardContent>
        </Card>
      )}

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            {t('notifications.title', { defaultValue: 'Notification Preferences' })}
          </CardTitle>
          <CardDescription>
            {t('notifications.description', { defaultValue: "Choose which notifications you'd like to receive." })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div className="space-y-0.5 flex-1">
                <div className="font-medium text-sm sm:text-base">{t('practitionerNotifications.newBookingEmail')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('practitionerNotifications.newBookingEmailDescription')}</div>
              </div>
              <Switch checked={notificationPrefs.new_booking_email} onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, new_booking_email: checked }))} />
            </div>
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div className="space-y-0.5 flex-1">
                <div className="font-medium text-sm sm:text-base">{t('practitionerNotifications.bookingCancellationEmail')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('practitionerNotifications.bookingCancellationEmailDescription')}</div>
              </div>
              <Switch checked={notificationPrefs.booking_cancellation_email} onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, booking_cancellation_email: checked }))} />
            </div>
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div className="space-y-0.5 flex-1">
                <div className="font-medium text-sm sm:text-base">{t('practitionerNotifications.clientMessageEmail')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('practitionerNotifications.clientMessageEmailDescription')}</div>
              </div>
              <Switch checked={notificationPrefs.client_message_email} onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, client_message_email: checked }))} />
            </div>
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div className="space-y-0.5 flex-1">
                <div className="font-medium text-sm sm:text-base">{t('practitionerNotifications.payoutNotificationEmail')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('practitionerNotifications.payoutNotificationEmailDescription')}</div>
              </div>
              <Switch checked={notificationPrefs.payout_notification_email} onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, payout_notification_email: checked }))} />
            </div>
          </div>

          <SmsConsentForm
            smsAppointmentReminder={notificationPrefs.sms_appointment_reminder}
            smsBookingUpdate={notificationPrefs.sms_booking_update}
            onSmsAppointmentReminderChange={(checked) => {
              const now = new Date().toISOString();
              setNotificationPrefs(prev => ({
                ...prev,
                sms_appointment_reminder: checked,
                sms_consent_at: checked && !prev.sms_consent_at ? now : prev.sms_consent_at,
              }));
              logConsent({
                userId: practitionerId,
                userType: 'practitioner',
                consentType: 'sms_appointment_reminder',
                consentVersion: '2026-02-18',
                action: checked ? 'granted' : 'revoked',
              });
            }}
            onSmsBookingUpdateChange={(checked) => {
              const now = new Date().toISOString();
              setNotificationPrefs(prev => ({
                ...prev,
                sms_booking_update: checked,
                sms_consent_at: checked && !prev.sms_consent_at ? now : prev.sms_consent_at,
              }));
              logConsent({
                userId: practitionerId,
                userType: 'practitioner',
                consentType: 'sms_booking_update',
                consentVersion: '2026-02-18',
                action: checked ? 'granted' : 'revoked',
              });
            }}
          />

          <Button onClick={saveNotificationPreferences} disabled={notifSaving} className="w-full sm:w-auto">
            {notifSaving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-pulse" />
                {t('notifications.saving', { defaultValue: 'Saving...' })}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('notifications.savePreferences', { defaultValue: 'Save Preferences' })}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Google Calendar Integration Card */}
      <GoogleCalendarIntegration />

      {/* Referral Settings */}
      <PractitionerReferralSettings />

      {/* Data & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            {t('dataPrivacy.title', { ns: 'settings', defaultValue: 'Data & Privacy' })}
          </CardTitle>
          <CardDescription>
            {t('dataPrivacy.description', { ns: 'settings', defaultValue: 'Manage your data and privacy preferences' })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-destructive/20 rounded-lg p-4 space-y-3">
            <div>
              <h4 className="font-medium text-sm sm:text-base">
                {t('dataPrivacy.deleteRecordings', { ns: 'settings', defaultValue: 'Delete All Recordings & Transcripts' })}
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {t('dataPrivacy.deleteRecordingsDescription', { ns: 'settings', defaultValue: 'Permanently delete all session recordings and transcripts you have uploaded' })}
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-2">
                  <Trash2 className="w-4 h-4" />
                  {t('dataPrivacy.deleteRecordings', { ns: 'settings', defaultValue: 'Delete All Recordings & Transcripts' })}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('dataPrivacy.deleteRecordingsConfirmTitle', { ns: 'settings', defaultValue: 'Delete All Recordings & Transcripts?' })}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('dataPrivacy.deleteRecordingsConfirmDescription', { ns: 'settings', defaultValue: 'This will permanently delete all session recordings, transcripts, and associated AI analyses. This action cannot be undone.' })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {t('dataPrivacy.cancel', { ns: 'settings', defaultValue: 'Cancel' })}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async () => {
                      setIsSaving(true);
                      try {
                        const { error } = await supabase
                          .from('session_transcripts')
                          .delete()
                          .eq('practitioner_id', practitionerId);
                        if (error) throw error;
                        logDataProcessing({
                          actorId: practitionerId,
                          actorType: 'practitioner',
                          action: 'delete',
                          resourceType: 'session_transcript',
                          details: 'Practitioner deleted all session recordings and transcripts',
                        });
                        toast({
                          title: t('dataPrivacy.deleteSuccess', { ns: 'settings', defaultValue: 'Successfully Deleted' }),
                          description: t('dataPrivacy.deleteSuccessRecordingsDescription', { ns: 'settings', defaultValue: 'All session recordings and transcripts have been deleted.' }),
                        });
                      } catch (error: any) {
                        toast({
                          variant: 'destructive',
                          title: t('dataPrivacy.deleteFailed', { ns: 'settings', defaultValue: 'Deletion Failed' }),
                          description: error.message || t('dataPrivacy.deleteFailedDescription', { ns: 'settings', defaultValue: 'Failed to delete data. Please try again.' }),
                        });
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {t('dataPrivacy.confirm', { ns: 'settings', defaultValue: 'Yes, Delete Everything' })}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* DSAR Data Rights Section */}
          <DataRightsSection userId={practitionerId} userType="practitioner" userEmail={profile?.email || ''} />
        </CardContent>
      </Card>

      {/* Dashboard Guide Card */}
      <Card>
        <CardHeader>
          <CardTitle>Dashboard Guide</CardTitle>
          <CardDescription>Restart the dashboard walkthrough tutorial to learn about all features.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={resetOnboardingTour}
            className="w-full sm:w-auto"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Restart Guide
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PractitionerSettings;
